import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateScenario.module.css';
import { api } from '../../api';
import { diceIcon } from '../../assets/icons';
import examplePrompts from '../../data/example_scenario_prompts.json';

interface FormData {
  description: string;
}

// Loading animation component
const LoadingAnimation = ({ progress }: { progress: number }) => {
  return (
    <div className={styles.loadingOverlay}>
      <div className={styles.loadingContent}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinnerRing}></div>
          <div className={styles.spinnerRing}></div>
          <div className={styles.spinnerRing}></div>
        </div>
        <div className={styles.loadingText}>
          <span>Creating your scenario</span>
          <span className={styles.loadingDots}>
            <span className={styles.dot}>.</span>
            <span className={styles.dot}>.</span>
            <span className={styles.dot}>.</span>
          </span>
        </div>
        <div className={styles.loadingSubtext}>This may take a few seconds</div>
        <div className={styles.progressBarContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

function CreateScenario() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [selectedExamples, setSelectedExamples] = useState<typeof examplePrompts>([]);

  // Get initial random examples
  useEffect(() => {
    getRandomExamples();
  }, []);

  const getRandomExamples = () => {
    const shuffled = [...examplePrompts].sort(() => 0.5 - Math.random());
    setSelectedExamples(shuffled.slice(0, 3));
  };

  const getRandomPrompt = () => {
    const randomIndex = Math.floor(Math.random() * examplePrompts.length);
    const randomPrompt = examplePrompts[randomIndex];
    setFormData(prev => ({
      ...prev,
      description: randomPrompt.description
    }));
  };

  // Update loading progress during submission
  useEffect(() => {
    if (isSubmitting) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          // Cap at 90% to avoid giving false impression of completion
          return prev < 90 ? prev + 1 : prev;
        });
      }, 100);
      
      return () => clearInterval(interval);
    } else {
      setLoadingProgress(0);
    }
  }, [isSubmitting]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleExampleClick = (description: string) => {
    setFormData(prev => ({
      ...prev,
      description
    }));
  };

  // Color classes for example prompts
  const colorClasses = [styles.examplePromptPink, styles.examplePromptCyan, styles.examplePromptYellow];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Create the scenario definition using the API
      const scenarioDefinition = await api.scenarioDefinitions.create({
        description: formData.description
      });
      
      // Navigate to the scenario definition detail page
      navigate(`/scenarios/${scenarioDefinition.id}`);
    } catch (err: any) {
      setError('Failed to create scenario');
      console.error('Error creating scenario:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createContainer}>
      {isSubmitting && <LoadingAnimation progress={loadingProgress} />}
      
      <form className={styles.createForm} onSubmit={handleSubmit}>
        {error && <div className={styles.error}>{error}</div>}
        
        <div className={styles.inputGroup}>
          <div className={styles.examplesSection}>
            <div className={styles.examples}>
              <div 
                className={`${styles.examplePrompt} ${styles.randomButton}`}
                onClick={getRandomPrompt}
              >
                <img src={diceIcon} alt="Random" className={styles.diceIconImg} />
              </div>
              {selectedExamples.map((example, index) => (
                <div 
                  key={index}
                  className={`${styles.examplePrompt} ${colorClasses[index % colorClasses.length]}`}
                  onClick={() => handleExampleClick(example.description)}
                >
                  {example.title}
                </div>
              ))}
            </div>
          </div>
          <textarea
            name="description"
            placeholder="Briefly describe your scenario. What is the setting? Who are the initial characters?"
            maxLength={280}
            value={formData.description}
            onChange={handleChange}
            required
          />
          {formData.description.length > 210 && (
            <span className={styles.charCount}>{formData.description.length}/280</span>
          )}
        </div>

        <p className={styles.helperText}>
          Don't worry! You'll have a chance to elaborate and expand your scenario details.
        </p>

        <button 
          type="submit" 
          className={styles.createButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Start Creating...'}
        </button>
      </form>
    </div>
  );
}

export default CreateScenario;